import { alpha, type SxProps, type Theme } from "@mui/material/styles";

import { customShadows } from "@/theme/customTokens";

export const root: SxProps<Theme> = {
  position: "relative",
  minHeight: "100dvh",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  backgroundImage: "url(/images/auth-bg.jpg)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  px: { xs: 2, sm: 2.5 },
  py: { xs: 3, sm: 5 },
  boxSizing: "border-box",
};

export const overlay = (theme: Theme): SxProps<Theme> => ({
  position: "absolute",
  inset: 0,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.62)}, ${alpha(theme.palette.primary.main, 0.48)})`,
});

export const card =
  (theme: Theme): SxProps<Theme> =>
  ({
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 920,
    maxHeight: { xs: "none", md: "88vh" },
    bgcolor: "background.paper",
    borderRadius: 3.25,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: customShadows.authCard,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  });

export const cardHeader: SxProps<Theme> = (theme) => ({
  flex: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  px: { xs: 2, sm: 3.5 },
  py: { xs: 2, sm: 2.75 },
  borderBottom: 1,
  borderColor: "divider",
});

export const headerBrand: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  minWidth: 0,
});

export const headerLogo: SxProps<Theme> = {
  height: 34,
  width: "auto",
  objectFit: "contain",
  flex: "none",
};

export const headerTitle: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: "1.125rem",
  color: "primary.dark",
};

export const headerSubtitle: SxProps<Theme> = {
  fontSize: "0.75rem",
  color: "text.secondary",
  mt: 0.25,
};

export const closeButton =
  (theme: Theme): SxProps<Theme> =>
  ({
    width: 38,
    height: 38,
    borderRadius: 1.5,
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "text.secondary",
    textDecoration: "none",
    flex: "none",
    transition: theme.transitions.create(["color", "border-color", "background-color"], {
      duration: theme.transitions.duration.shorter,
    }),
    "&:hover": {
      color: "primary.main",
      borderColor: alpha(theme.palette.primary.main, 0.4),
      bgcolor: alpha(theme.palette.primary.main, 0.06),
    },
  });

export const cardBody: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  overflow: "hidden",
  minHeight: 0,
};

export const sideNav: SxProps<Theme> = {
  display: { xs: "none", md: "flex" },
  width: 210,
  flex: "none",
  flexDirection: "column",
  gap: 0.25,
  borderInlineStart: 1,
  borderColor: "divider",
  px: 1.75,
  py: 2.5,
  overflowY: "auto",
};

export const navItem =
  (active: boolean): SxProps<Theme> =>
  (theme) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    px: 1.5,
    py: 1.25,
    borderRadius: 1.25,
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: active ? "primary.main" : "text.secondary",
    textDecoration: "none",
    cursor: "pointer",
    bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : "transparent",
    transition: theme.transitions.create(["background-color", "color"], {
      duration: theme.transitions.duration.shorter,
    }),
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      color: "primary.main",
    },
  });

export const navNumber =
  (active: boolean): SxProps<Theme> =>
  () => ({
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.6875rem",
    fontWeight: 700,
    flex: "none",
    bgcolor: active ? "primary.main" : "background.default",
    color: active ? "primary.contrastText" : "text.secondary",
  });

export const content: SxProps<Theme> = {
  flex: 1,
  overflowY: "auto",
  px: { xs: 2, sm: 3.75 },
  py: { xs: 1, sm: 0.75 },
  pb: { xs: 3, sm: 3.5 },
};

export const intro: SxProps<Theme> = {
  fontSize: "0.84375rem",
  lineHeight: 1.9,
  color: "text.secondary",
  py: { xs: 2, sm: 2.5 },
  pb: { xs: 1, sm: 0.5 },
};

export const section: SxProps<Theme> = (theme) => ({
  scrollMarginTop: theme.spacing(2.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.25),
  py: 2.75,
  borderBottom: 1,
  borderColor: "divider",
  "&:last-of-type": {
    borderBottom: "none",
    pb: 0,
  },
});

export const sectionTitle: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  fontWeight: 700,
  fontSize: "0.96875rem",
});

export const sectionBody: SxProps<Theme> = {
  fontSize: "0.84375rem",
  lineHeight: 1.9,
  color: "text.secondary",
};

export const supportBox: SxProps<Theme> = (theme) => ({
  mt: 0.75,
  p: 2.25,
  borderRadius: 1.75,
  bgcolor: "background.default",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
});

export const supportTitle: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 600,
};

export const cardFooter: SxProps<Theme> = (theme) => ({
  flex: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  px: { xs: 2, sm: 3.5 },
  py: 2,
  borderTop: 1,
  borderColor: "divider",
});

export const copyright: SxProps<Theme> = {
  fontSize: "0.75rem",
  color: "text.secondary",
};
